/**
 * Whether the reading page that hosts the viewer is still on screen (D7-7986)
 *
 * Plain terms
 * -----------
 * When the reader leaves a reading — the viewer's Close button, a breadcrumb,
 * the browser's Back — the viewer and the scoring form disappear together. On
 * its way out the form still asks the viewer to "stop highlighting this
 * lesion", but the viewer is already gone, so nobody answers and a few seconds
 * later an error is logged for a request that no longer mattered. Knowing the
 * page is gone lets that last request be dropped quietly.
 *
 * Technical
 * ---------
 * `ReadingTaskViewerPage` marks the viewer gone in a `useLayoutEffect`
 * cleanup, which runs inside the same React commit that removes the iframe
 * and unmounts the form. The form's unmount-time `deactivate` reaches D7 over
 * the BroadcastChannel, i.e. in a later task, so the flag is always set by
 * then. The default is "present": pages without this hook (QC, simple viewer,
 * the detached form window) behave exactly as before. Module state — one
 * reading page per D7 window. Read through `call(isViewerGone)` so workers
 * stay `@test/saga`-testable.
 *
 * NOTE: the flag stays "gone" until a reading page mounts again, and only
 * `ReadingTaskViewerPage` resets it today. Any new page that hosts a scoring
 * form next to the viewer must call `markViewerPresent` on mount and
 * `markViewerGone` in a layout-effect cleanup, or its activate/deactivate
 * requests are silently skipped after the reader has left one reading.
 */
let viewerGone = false;

export const markViewerPresent = (): void => {
  viewerGone = false;
};

export const markViewerGone = (): void => {
  viewerGone = true;
};

export const isViewerGone = (): boolean => viewerGone;
